import { makeAutoObservable, runInAction } from "mobx";
import {
  superadminListServiceCenters,
  superadminGetServiceCenter,
  superadminGetServiceCentersSummary,
} from "@pro-sto/api-client";
import type {
  SuperadminServiceCenterItemDto,
  SuperadminServiceCenterSummaryDto,
  PaginationMetaDto,
} from "@pro-sto/api-client";
import { toastStore } from "./ToastStore";

export type SortDirection = "ascending" | "descending";

export interface SortDescriptor {
  column: string;
  direction: SortDirection;
}

export interface RangeFilter {
  from?: number;
  to?: number;
}

export interface ContractorsFilters {
  rating: RangeFilter;
  revenue: RangeFilter;
  commission: RangeFilter;
  pendingPayout: RangeFilter;
}

const emptyFilters = (): ContractorsFilters => ({
  rating: {},
  revenue: {},
  commission: {},
  pendingPayout: {},
});

export interface ContractorDetail {
  uuid: string;
  name: string;
  address: string;
  postsCount?: number;
  bookingsCount?: number;
  isPublished?: boolean;
  contacts?: { phone?: string; email?: string; website?: string };
  description?: string | null;
  logo_url?: string | null;
  location?: { lat: number; lon: number } | null;
}

/** Cache key = "YYYY-MM" */
function monthKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

interface MonthCache {
  contractors: SuperadminServiceCenterItemDto[];
  meta: PaginationMetaDto | null;
  summary: SuperadminServiceCenterSummaryDto | null;
}

class ContractorsStore {
  // ── Per-month cache ──
  private monthDataCache: Map<string, MonthCache> = new Map();

  // List data (current view)
  contractors: SuperadminServiceCenterItemDto[] = [];
  meta: PaginationMetaDto | null = null;
  isLoading = false;
  isLoadingMore = false;

  /**
   * True пока пользователь активно печатает в поле поиска
   * (есть отложенный debounce). Используется, чтобы НЕ блокировать
   * UI пока ввод не «осел».
   */
  isUserTypingSearch = false;

  /** Monotonic id used to discard stale list responses */
  private listRequestSeq = 0;
  /** Monotonic id used to discard stale summary responses */
  private summaryRequestSeq = 0;

  // Summary data (current view)
  summary: SuperadminServiceCenterSummaryDto | null = null;
  isSummaryLoading = false;

  // Detail
  currentContractor: ContractorDetail | null = null;
  isDetailLoading = false;

  // Filters & search
  search = "";
  filters: ContractorsFilters = emptyFilters();
  statusFilter: "all" | "active" | "inactive" = "all";
  sortDescriptor: SortDescriptor = {
    column: "rating",
    direction: "descending",
  };

  // Month navigation
  activeMonth: Date = (() => {
    const d = new Date();
    d.setDate(1);
    return d;
  })();

  // Pagination
  page = 1;
  readonly limit = 20;

  // Selection
  selectedRows: Set<string> = new Set();

  constructor() {
    makeAutoObservable(this, {
      //@ts-ignore
      monthDataCache: false,
      //@ts-ignore
      listRequestSeq: false,
      //@ts-ignore
      summaryRequestSeq: false,
    });
  }

  // ── Computed ──

  /** True while any active list/summary network request is in flight */
  get isFetching(): boolean {
    return this.isLoading || this.isLoadingMore || this.isSummaryLoading;
  }

  /**
   * True если UI следует считать «занятым» — идёт сетевой запрос
   * и пользователь при этом не находится в процессе ввода поиска.
   * Используется для дизейбла фильтров, сортировки, навигации по месяцам.
   */
  get isUiBusy(): boolean {
    return this.isFetching && !this.isUserTypingSearch;
  }

  get visibleContractors(): SuperadminServiceCenterItemDto[] {
    let items = this.contractors;

    if (this.statusFilter === "active") {
      items = items.filter((c) => c.isPublished !== false);
    } else if (this.statusFilter === "inactive") {
      items = items.filter((c) => c.isPublished === false);
    }

    // Если по числовому полю выставлен фильтр — строки с отсутствующим
    // значением (null/undefined → «—») не должны попадать в результат.
    const f = this.filters;
    const numericFilters: Array<[keyof ContractorsFilters, RangeFilter]> = [
      ["rating", f.rating],
      ["revenue", f.revenue],
      ["commission", f.commission],
      ["pendingPayout", f.pendingPayout],
    ];
    for (const [field, range] of numericFilters) {
      if (range.from === undefined && range.to === undefined) continue;
      items = items.filter((c) => {
        const val = (c as Record<string, unknown>)[field as string];
        if (typeof val !== "number" || !Number.isFinite(val)) return false;
        if (range.from !== undefined && val < range.from) return false;
        if (range.to !== undefined && val > range.to) return false;
        return true;
      });
    }

    // Client-side string sort for name/address (API does not support these fields)
    const { column, direction } = this.sortDescriptor;
    if (column === "name" || column === "address") {
      items = [...items].sort((a, b) => {
        const va = ((a as Record<string, unknown>)[column] as string) ?? "";
        const vb = ((b as Record<string, unknown>)[column] as string) ?? "";
        const cmp = va.localeCompare(vb, "ru");
        return direction === "ascending" ? cmp : -cmp;
      });
    }

    return items;
  }

  get hasNextPage(): boolean {
    return this.meta?.hasNextPage ?? false;
  }

  get allSelected(): boolean {
    return (
      this.visibleContractors.length > 0 &&
      this.visibleContractors.every((c) => this.selectedRows.has(c.uuid))
    );
  }

  get someSelected(): boolean {
    return (
      this.visibleContractors.some((c) => this.selectedRows.has(c.uuid)) &&
      !this.allSelected
    );
  }

  get selectedItems(): SuperadminServiceCenterItemDto[] {
    return this.visibleContractors.filter((c) => this.selectedRows.has(c.uuid));
  }

  get totalDebt(): number {
    return this.selectedItems.reduce(
      (sum, c) => sum + (c.pendingPayout ?? 0),
      0,
    );
  }

  get payableCount(): number {
    return this.selectedItems.filter((c) => c.pendingPayout > 0).length;
  }

  get hasActiveFilters(): boolean {
    const f = this.filters;
    return !!(
      this.statusFilter !== "all" ||
      f.rating.from !== undefined ||
      f.rating.to !== undefined ||
      f.revenue.from !== undefined ||
      f.revenue.to !== undefined ||
      f.commission.from !== undefined ||
      f.commission.to !== undefined ||
      f.pendingPayout.from !== undefined ||
      f.pendingPayout.to !== undefined
    );
  }

  private get sortParam(): string | undefined {
    const col = this.sortDescriptor.column;
    // name/address are sorted client-side; API does not support these fields
    if (col === "name" || col === "address") return undefined;
    const dir = this.sortDescriptor.direction === "descending" ? "-" : "";
    return `${dir}${col}`;
  }

  private apiListParamsForPage(page: number, month?: Date) {
    const f = this.filters;
    const m = month ?? this.activeMonth;
    return {
      month: m.getMonth() + 1,
      year: m.getFullYear(),
      q: this.search || undefined,
      sort: this.sortParam,
      page,
      limit: this.limit,
      filterRatingGte: f.rating.from,
      filterRatingLte: f.rating.to,
      filterRevenueGte: f.revenue.from,
      filterRevenueLte: f.revenue.to,
      filterCommissionGte: f.commission.from,
      filterCommissionLte: f.commission.to,
      filterPendingPayoutGte: f.pendingPayout.from,
      filterPendingPayoutLte: f.pendingPayout.to,
      filterStatusEq:
        this.statusFilter !== "all"
          ? (this.statusFilter as "active" | "inactive")
          : undefined,
    };
  }

  private apiSummaryParamsForMonth(month?: Date) {
    const f = this.filters;
    const m = month ?? this.activeMonth;
    return {
      month: m.getMonth() + 1,
      year: m.getFullYear(),
      q: this.search || undefined,
      filterRatingGte: f.rating.from,
      filterRatingLte: f.rating.to,
      filterRevenueGte: f.revenue.from,
      filterRevenueLte: f.revenue.to,
      filterCommissionGte: f.commission.from,
      filterCommissionLte: f.commission.to,
      filterPendingPayoutGte: f.pendingPayout.from,
      filterPendingPayoutLte: f.pendingPayout.to,
      filterStatusEq:
        this.statusFilter !== "all"
          ? (this.statusFilter as "active" | "inactive")
          : undefined,
    };
  }

  // ── Actions ──

  /** Load first page of contractors (reset) */
  loadContractors = async () => {
    const reqId = ++this.listRequestSeq;
    this.isLoading = true;
    try {
      const response = await superadminListServiceCenters(
        this.apiListParamsForPage(1),
      );
      // Игнорируем результаты устаревших запросов
      if (reqId !== this.listRequestSeq) return;
      runInAction(() => {
        this.contractors = response.data ?? [];
        this.meta = response.meta ?? null;
        this.page = 1;
        this.isLoading = false;
        this.saveToCache();
      });
    } catch {
      if (reqId !== this.listRequestSeq) return;
      runInAction(() => {
        this.isLoading = false;
      });
      toastStore.showError("Не удалось загрузить список контрагентов");
    }
  };

  loadMore = async () => {
    if (this.isLoadingMore || !this.hasNextPage) return;
    const reqId = ++this.listRequestSeq;
    this.isLoadingMore = true;
    const nextPage = this.page + 1;
    try {
      const response = await superadminListServiceCenters(
        this.apiListParamsForPage(nextPage),
      );
      if (reqId !== this.listRequestSeq) return;
      runInAction(() => {
        const newItems = response.data ?? [];
        const existing = new Set(this.contractors.map((c) => c.uuid));
        const unique = newItems.filter((c) => !existing.has(c.uuid));
        this.contractors = [...this.contractors, ...unique];
        this.meta = response.meta ?? null;
        this.page = nextPage;
        this.isLoadingMore = false;
        this.saveToCache();
      });
    } catch {
      if (reqId !== this.listRequestSeq) return;
      runInAction(() => {
        this.isLoadingMore = false;
      });
    }
  };

  loadSummary = async () => {
    const reqId = ++this.summaryRequestSeq;
    this.isSummaryLoading = true;
    try {
      const response = await superadminGetServiceCentersSummary(
        this.apiSummaryParamsForMonth(),
      );
      if (reqId !== this.summaryRequestSeq) return;
      runInAction(() => {
        this.summary = response;
        this.isSummaryLoading = false;
        this.saveToCache();
      });
    } catch {
      if (reqId !== this.summaryRequestSeq) return;
      runInAction(() => {
        this.isSummaryLoading = false;
      });
      toastStore.showError("Не удалось загрузить сводные метрики");
    }
  };

  loadAll = async () => {
    await Promise.all([this.loadContractors(), this.loadSummary()]);
  };

  loadContractor = async (uuid: string) => {
    this.isDetailLoading = true;
    try {
      const response = await superadminGetServiceCenter({ uuid });
      runInAction(() => {
        this.currentContractor = response as unknown as ContractorDetail;
        this.isDetailLoading = false;
      });
    } catch {
      runInAction(() => {
        this.isDetailLoading = false;
      });
      toastStore.showError("Не удалось загрузить данные контрагента");
    }
  };

  // ── Month cache ──

  private saveToCache() {
    const key = monthKey(this.activeMonth);
    this.monthDataCache.set(key, {
      contractors: [...this.contractors],
      meta: this.meta,
      summary: this.summary,
    });
  }

  private restoreFromCache(month: Date): boolean {
    const cached = this.monthDataCache.get(monthKey(month));
    if (!cached) return false;
    this.contractors = cached.contractors;
    this.meta = cached.meta;
    this.summary = cached.summary;
    this.page = cached.meta?.page ?? 1;
    return true;
  }

  /** Prefetch adjacent month data in background */
  private prefetchAdjacentMonth(direction: 1 | -1) {
    const adj = new Date(this.activeMonth);
    adj.setMonth(adj.getMonth() + direction);
    const key = monthKey(adj);
    if (this.monthDataCache.has(key)) return;

    // Fire-and-forget background fetch
    Promise.all([
      superadminListServiceCenters(this.apiListParamsForPage(1, adj)),
      superadminGetServiceCentersSummary(this.apiSummaryParamsForMonth(adj)),
    ])
      .then(([listResp, summaryResp]) => {
        runInAction(() => {
          this.monthDataCache.set(key, {
            contractors: listResp.data ?? [],
            meta: listResp.meta ?? null,
            summary: summaryResp,
          });
        });
      })
      .catch(() => {
        /* prefetch failure is non-critical */
      });
  }

  /** Invalidate cache (when filters/search/sort change) */
  private invalidateCache() {
    this.monthDataCache.clear();
  }

  // ── Setters ──

  setSearch = (value: string) => {
    this.search = value;
    this.page = 1;
    this.invalidateCache();
    this.loadAll();
  };

  setIsUserTypingSearch = (value: boolean) => {
    this.isUserTypingSearch = value;
  };

  setSortDescriptor = (descriptor: SortDescriptor) => {
    const isClientSideOnly =
      descriptor.column === "name" || descriptor.column === "address";
    this.sortDescriptor = descriptor;
    this.page = 1;
    if (isClientSideOnly) {
      // No API change — visibleContractors computed handles sorting client-side
      return;
    }
    this.invalidateCache();
    this.loadContractors();
  };

  setFilters = (
    filters: ContractorsFilters,
    status: "all" | "active" | "inactive" = "all",
  ) => {
    this.filters = filters;
    this.statusFilter = status;
    this.page = 1;
    this.invalidateCache();
    this.loadAll();
  };

  resetFilters = () => {
    this.filters = emptyFilters();
    this.statusFilter = "all";
    this.page = 1;
    this.invalidateCache();
    this.loadAll();
  };

  setActiveMonth = (month: Date) => {
    // Reset UI state to clean baseline on month switch
    this.activeMonth = month;
    this.filters = emptyFilters();
    this.statusFilter = "all";
    this.sortDescriptor = { column: "rating", direction: "descending" };
    this.page = 1;
    this.selectedRows = new Set();
    // Do NOT call invalidateCache() here — cached entries for other months
    // are always stored in the clean (no-filter) state, so they stay valid.
    // Restoring from cache gives instant display; loadAll() refreshes in background.
    this.restoreFromCache(month);
    this.loadAll();
  };

  goToPrevMonth = () => {
    const n = new Date(this.activeMonth);
    n.setMonth(n.getMonth() - 1);
    this.setActiveMonth(n);
    this.prefetchAdjacentMonth(-1);
  };

  goToNextMonth = () => {
    const n = new Date(this.activeMonth);
    n.setMonth(n.getMonth() + 1);
    this.setActiveMonth(n);
    this.prefetchAdjacentMonth(1);
  };

  setPage = (page: number) => {
    this.page = page;
    this.loadContractors();
  };

  // ── Selection ──

  toggleAll = () => {
    if (this.allSelected) {
      this.selectedRows = new Set();
    } else {
      this.selectedRows = new Set(this.visibleContractors.map((c) => c.uuid));
    }
  };

  toggleRow = (uuid: string) => {
    const next = new Set(this.selectedRows);
    if (next.has(uuid)) next.delete(uuid);
    else next.add(uuid);
    this.selectedRows = next;
  };

  clearSelection = () => {
    this.selectedRows = new Set();
  };
}

export const contractorsStore = new ContractorsStore();
export type { ContractorsStore };
